import { isSupportActivity } from '../admin/supportTransport';
// Analytics service for tracking page views and events (Google Analytics + Meta Pixel)
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set' | 'consent',
      targetId: string | Date | Record<string, any>,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
    fbq: (command: string, event?: string, parameters?: any) => void;
    _fbq: any[];
  }
}

export interface GAEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

export interface GAPageView {
  page_title?: string;
  page_location?: string;
  page_path?: string;
}

class AnalyticsService {
  private trackingId: string | null = null;
  private metaPixelId: string | null = null;
  private isInitialized = false;
  private isScriptLoaded = false;
  private isMetaPixelLoaded = false;
  private initializationPromise: Promise<void> | null = null;

  constructor() {
    this.trackingId = (import.meta as any).env.VITE_GA_TRACKING_ID;
    this.metaPixelId = (import.meta as any).env.VITE_META_PIXEL_ID;
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (!this.trackingId) {
      return Promise.resolve();
    }

    if (typeof window === 'undefined') {
      return Promise.resolve();
    }

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    try {
      // Initialize Google Analytics if available
      if (this.trackingId) {
        await this.loadGoogleAnalyticsScript();
        this.configureGtag();
      }

      // Initialize Meta Pixel if available
      if (this.metaPixelId) {
        this.initializeMetaPixel();
      }

      this.isInitialized = true;

      if (this.trackingId) {
        window.gtag('event', 'analytics_initialized', {
          event_category: 'system',
          event_label: 'successful_initialization',
        });
      }
    } catch (error) {
      console.error('Analytics Service: Failed to initialize analytics:', error);
      this.initializationPromise = null;
      throw error;
    }
  }

  private loadGoogleAnalyticsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isScriptLoaded || window.gtag) {
        resolve();
        return;
      }

      // Step 1: Inject gtag initializer first
      const inlineScript = document.createElement('script');
      inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){ dataLayer.push(arguments); }
      `;
      document.head.appendChild(inlineScript);

      // Step 2: Load GA4 script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.trackingId}`;
      script.onload = () => {
        this.isScriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Google Analytics script'));
      };

      document.head.appendChild(script);
    });
  }

  private configureGtag(): void {
    if (typeof window === 'undefined' || !window.gtag) return;

    window.gtag('js', new Date());
    window.gtag('config', this.trackingId!, {
      send_page_view: false,
      enhanced_measurement: true,
      cookie_flags: 'SameSite=None;Secure',
      debug_mode: (import.meta as any).env.DEV,
      custom_map: {},
      allow_ad_personalization_signals: true,
    });
  }

  private initializeMetaPixel(): void {
    if (typeof window === 'undefined' || !this.metaPixelId) return;

    // Meta Pixel should already be loaded via the HTML script tag
    // Just ensure the fbq function is available and initialized
    if (window.fbq) {
      this.isMetaPixelLoaded = true;
      console.log('Meta Pixel initialized with ID:', this.metaPixelId);
    } else {
      console.warn(
        'Meta Pixel fbq function not found. Make sure the Meta Pixel script is loaded in the HTML.'
      );
    }
  }

  trackPageView(options: GAPageView = {}) {
    if (isSupportActivity()) return;
    if (!this.isInitialized) {
      return;
    }

    // Track in Google Analytics
    if (this.trackingId && window.gtag) {
      const pageData = {
        page_title: options.page_title || document.title,
        page_location: options.page_location || window.location.href,
        page_path: options.page_path || window.location.pathname,
      };
      window.gtag('event', 'page_view', pageData);
    }

    // Track in Meta Pixel (PageView is already tracked in HTML, but we can track additional page views)
    if (this.isMetaPixelLoaded && window.fbq) {
      window.fbq('track', 'PageView');
    }
  }

  trackEvent(event: GAEvent) {
    if (isSupportActivity()) return;
    if (!this.isInitialized) {
      return;
    }

    // Track in Google Analytics
    if (this.trackingId && window.gtag) {
      const eventData = {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
      };
      window.gtag('event', event.action, eventData);
    }

    // Track in Meta Pixel for specific events
    if (this.isMetaPixelLoaded && window.fbq) {
      const metaPixelData: any = {
        content_name: event.label,
      };

      // Only include value and currency for actual monetary events
      if (event.value && ['purchase', 'subscription', 'billing'].includes(event.category)) {
        metaPixelData.value = event.value;
        metaPixelData.currency = 'USD';
      }
      // Don't include currency for engagement metrics like time_on_page, scroll_depth, etc.

      this.trackMetaEvent(event.action, metaPixelData);
    }
  }

  private trackMetaEvent(eventName: string, parameters: any = {}) {
    if (this.isMetaPixelLoaded && window.fbq) {
      window.fbq('track', eventName, parameters);
    }
  }

  trackAuth(action: 'sign_in' | 'sign_up' | 'sign_out') {
    if (isSupportActivity()) return;
    this.trackEvent({ action, category: 'authentication' });

    // Track specific Meta pixel events for signup
    if (action === 'sign_up' && this.isMetaPixelLoaded && window.fbq) {
      window.fbq('track', 'CompleteRegistration', {
        content_name: 'User Registration',
        status: 'completed',
      });
    }
  }

  trackSubscription(amount: number, currency: string = 'USD', planType?: string) {
    if (isSupportActivity()) return;
    // Track in Google Analytics
    if (this.trackingId && window.gtag) {
      window.gtag('event', 'purchase', {
        transaction_id: `sub_${Date.now()}`,
        value: amount,
        currency: currency,
        items: [
          {
            item_name: planType || 'Subscription',
            price: amount,
            quantity: 1,
          },
        ],
      });
    }

    // Track in Meta Pixel
    if (this.isMetaPixelLoaded && window.fbq) {
      window.fbq('track', 'Purchase', {
        value: amount,
        currency: currency,
        content_name: planType || 'Subscription',
        content_type: 'product',
      });
    }
  }

  trackStrategy(action: string, label?: string) {
    this.trackEvent({ action, category: 'strategy', label });
  }

  trackCompany(action: string, label?: string) {
    this.trackEvent({ action, category: 'company', label });
  }

  trackExecution(action: string, label?: string) {
    this.trackEvent({ action, category: 'execution', label });
  }

  trackOrganization(action: string, label?: string) {
    this.trackEvent({ action, category: 'organization', label });
  }

  trackReport(action: string, label?: string) {
    this.trackEvent({ action, category: 'reports', label });
  }

  trackEngagement(action: string, label?: string, value?: number) {
    this.trackEvent({ action, category: 'engagement', label, value });
  }

  trackError(error: string, fatal = false) {
    this.trackEvent({
      action: 'exception',
      category: 'error',
      label: error,
      value: fatal ? 1 : 0,
    });
  }

  setUserProperties(properties: Record<string, string>) {
    if (isSupportActivity()) return;
    if (!this.isInitialized || !window.gtag) return;
    window.gtag('set', 'user_properties', properties);
  }

  isEnabled(): boolean {
    return this.isInitialized && (this.trackingId !== null || this.metaPixelId !== null);
  }

  isMetaPixelEnabled(): boolean {
    return this.isInitialized && this.isMetaPixelLoaded && !!this.metaPixelId;
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();
